import { useCallback, useEffect, useState } from "react";
import useDirection from "hooks/useDirection";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LocalStorage from "utils/localstorage.util";
import {
  getPartnerTokenFromStorage,
  isYeloAppSession,
  PARTNER_CHECKOUT_STORAGE_KEY,
  partnerHomePath,
  withPartnerToken,
} from "utils/partnerSession.util";
import { notifyApp } from "utils/yeloAppBridge.util";
import BookingService from "services/booking.service";
import { useAppDispatch } from "redux/hooks";
import { paymentActions } from "redux/slices/payment";
import { fetchPartnerCheckoutSession } from "./partnerCheckoutSession.util";
import {
  getHyperPayCheckout,
  type HyperpayConfigShape,
} from "utils/hyperpayCheckout.util";
import PartnerPayfortCheckout from "./PartnerPayfortCheckout";
import PartnerStripeCheckout from "./PartnerStripeCheckout";
import PartnerHyperPayCheckout from "./PartnerHyperPayCheckout";
import PartnerPaymentSuccess from "./PartnerPaymentSuccess";
import "./PaymentForm.scss";
import {
  rememberPaymentContext,
  trackPaymentFailed,
  trackPaymentRecoverySelected,
  trackWebviewExited,
} from "services/mixpanel.service";
import { shouldEnableYeloAppAnalytics } from "utils/analytics.util";
import { goBack } from "utils/navigation.util";

const BackArrowIcon = () => (
  <svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M15 6l-6 6 6 6" />
  </svg>
);

type PartnerCheckoutPayload = {
  trip_id: string;
  price?: number;
  currency?: string;
  payfort_form?: string;
  client_secret?: string;
  stripe_client_secret?: string;
  payment_gateway?: string;
  platform?: string;
  form?: string;
  hyperpay?: HyperpayConfigShape | null;
  hyperpayBillingGate?: boolean;
};

const applyCheckoutPayload = (
  data: Record<string, any>,
  tripId?: string | null
): PartnerCheckoutPayload => ({
  trip_id: data.trip_id || tripId || "",
  price: data.price,
  currency: data.currency,
  payfort_form: data.payfort_form || data.form || data.client_secret,
  client_secret: data.client_secret || data.form,
  stripe_client_secret: data.stripe_client_secret || data.client_secret,
  form: data.form,
  payment_gateway: data.payment_gateway,
  platform: data.platform,
  hyperpay: data.hyperpay,
  hyperpayBillingGate: data.hyperpayBillingGate,
});

const hasRenderableCheckout = (payload: PartnerCheckoutPayload | null) => {
  if (!payload?.trip_id) return false;

  const payfortForm =
    payload.payfort_form || payload.client_secret || payload.form || "";
  const stripeClientSecret =
    payload.stripe_client_secret || payload.client_secret || "";
  const isStripe =
    payload.payment_gateway === "Stripe" ||
    Boolean(stripeClientSecret && payload.payment_gateway !== "HyperPay");
  const isHyperPay =
    payload.payment_gateway === "HyperPay" || payload.platform === "hyper-pay";

  if (isStripe && stripeClientSecret) return true;
  if (isHyperPay && getHyperPayCheckout(payload.hyperpay)) return true;
  if (payfortForm) return true;

  return false;
};

export default function PaymentForm() {
  const { t, i18n } = useTranslation("translation");
  const direction = useDirection();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [payload, setPayload] = useState<PartnerCheckoutPayload | null>(null);
  const [loadingPayment, setLoadingPayment] = useState(true);
  const [loadError, setLoadError] = useState("");

  /* Warm the connection to HyperPay while the checkout session is still
     being fetched, so paymentWidgets.js starts downloading on an open
     connection the moment the checkout id arrives. Host is per environment. */
  useEffect(() => {
    ["https://eu-prod.oppwa.com", "https://eu-test.oppwa.com"].forEach((href) => {
      if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) return;
      const link = document.createElement("link");
      link.rel = "preconnect";
      link.href = href;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    });
  }, []);

  const paymentStatus = searchParams.get("type");
  const bookingId = searchParams.get("bookingID");
  const errorMessage = searchParams.get("message");
  const orderId = searchParams.get("order");
  const gateway = searchParams.get("gateway");

  const hydratePrice = useCallback(
    (checkout: PartnerCheckoutPayload) => {
      if (checkout.price != null) {
        dispatch(
          paymentActions.setPrice({
            price: checkout.price,
            currency: checkout.currency || "SAR",
          })
        );
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (paymentStatus !== "error" || !shouldEnableYeloAppAnalytics()) return;
    const text = (errorMessage || "").toLowerCase();
    trackPaymentFailed({
      reason_category: text.includes("cancel") ? "cancelled" : "declined",
      unconfirmed: true,
    });
  }, [errorMessage, paymentStatus]);

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      localStorage.setItem("jtoken", `Bearer ${tokenFromUrl}`);
    }

    if (!isYeloAppSession()) {
      navigate(withPartnerToken("/not-authorize"), { replace: true });
      return;
    }

    if (paymentStatus === "success" || paymentStatus === "error") {
      setLoadingPayment(false);
      return;
    }

    if (!orderId) {
      setLoadingPayment(false);
      navigate(withPartnerToken("/search-form#checkout"), { replace: true });
      return;
    }

    let cancelled = false;
    setLoadingPayment(true);
    setLoadError("");

    fetchPartnerCheckoutSession(orderId)
      .then(([data, error]: any) => {
        if (cancelled) return;

        if (!data) {
          setLoadingPayment(false);
          setLoadError(
            error?.response?.data?.message ||
              String(
                t("paymentFormLoadError", "Payment form could not be loaded.")
              )
          );
          return;
        }

        const checkout = applyCheckoutPayload(data, orderId);
        LocalStorage.setItem(PARTNER_CHECKOUT_STORAGE_KEY, checkout);
        setPayload(checkout);
        hydratePrice(checkout);
        if (shouldEnableYeloAppAnalytics()) {
          const hyperpayCheckout = getHyperPayCheckout(checkout.hyperpay);
          rememberPaymentContext({
            bookingId: String(checkout.trip_id || orderId || ""),
            requestId:
              hyperpayCheckout?.config?.checkoutId ||
              hyperpayCheckout?.config?.checkoutIdCard ||
              String(checkout.trip_id || orderId || ""),
            amount: Number(checkout.price),
            currency: checkout.currency,
          });
        }
        setLoadingPayment(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadingPayment(false);
        setLoadError(
          String(t("paymentFormLoadError", "Payment form could not be loaded."))
        );
      });

    return () => {
      cancelled = true;
    };
  }, [hydratePrice, navigate, orderId, paymentStatus, searchParams, t]);

  const payfortForm =
    payload?.payfort_form || payload?.client_secret || payload?.form || "";
  const stripeClientSecret =
    payload?.stripe_client_secret ||
    (gateway === "stripe" ? payload?.client_secret : "");
  const isStripeCheckout =
    payload?.payment_gateway === "Stripe" || gateway === "stripe";
  const isHyperPayCheckout =
    payload?.payment_gateway === "HyperPay" ||
    payload?.platform === "hyper-pay";

  if (paymentStatus === "success") {
    const id = bookingId || orderId;
    if (!id) {
      return (
        <div className="yp-payment" dir={direction}>
          <div className="yp-payment__state">
            <div className="yp-payment__state-card">
              <p className="yp-payment__state-title">
                <bdi>{t("paymentSuccessful", "Payment successful")}</bdi>
              </p>
              <p className="yp-payment__state-body">
                <bdi>
                  {errorMessage ||
                    t("bookingConfirmed", "Your booking is confirmed.")}
                </bdi>
              </p>
            </div>
          </div>
        </div>
      );
    }
    return <PartnerPaymentSuccess bookingId={id} />;
  }

  if (paymentStatus === "error") {
    return (
      <div className="yp-payment" dir={direction}>
        <div className="yp-payment__state">
          <div className="yp-payment__state-card">
            <p className="yp-payment__state-title is-error">
              <bdi>{t("paymentFailed", "Payment failed")}</bdi>
            </p>
            <p className="yp-payment__state-body">
              <bdi>{errorMessage || t("tryAgain", "Please try again.")}</bdi>
            </p>
            <div className="yp-payment__success-actions">
              {bookingId || orderId ? (
                <button
                  type="button"
                  className="yp-payment__submit"
                  onClick={() => {
                    if (shouldEnableYeloAppAnalytics()) {
                      trackPaymentRecoverySelected("try_again");
                    }
                    navigate(
                      withPartnerToken(
                        `/payment-form?order=${bookingId || orderId}`
                      ),
                      { replace: true }
                    );
                  }}
                >
                  {t("tryAgain", "Try again")}
                </button>
              ) : null}
              <button
                type="button"
                className="yp-payment__submit yp-payment__submit--outline"
                onClick={() => {
                  if (shouldEnableYeloAppAnalytics()) {
                    trackWebviewExited("payment_failed");
                  }
                  if (!notifyApp("close")) navigate(partnerHomePath());
                }}
              >
                {t("backToYeloPlus", "Back to Yelo app")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadingPayment || !payload || !hasRenderableCheckout(payload)) {
    return (
      <div className="yp-payment" dir={direction}>
        <div className="yp-payment__state">
          <div className="yp-payment__state-card">
            {loadError ? (
              <p className="yp-payment__state-title is-error">
                <bdi>{loadError}</bdi>
              </p>
            ) : (
              <>
                <span className="yp-payment__spinner" aria-hidden="true" />
                <p className="yp-payment__state-body">
                  <bdi>{t("loadingPayment", "Loading payment")}</bdi>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="yp-payment" dir={direction}>
      <div className="yp-payment__topbar">
        <button
          type="button"
          className="yp-payment__back"
          onClick={() =>
            goBack(navigate, withPartnerToken("/search-form#checkout"))
          }
        >
          <BackArrowIcon />
        </button>
        <h1 className="yp-payment__title">
          {String(t("checkout", "Checkout"))}
        </h1>
      </div>

      <div className="yp-payment__container">
        <div className="yp-payment__card yp-payment__amount">
          <span className="yp-payment__amount-label">
            {t("Amount", "Amount")}
          </span>
          <span className="yp-payment__amount-value" dir="ltr">
            {Number(payload.price || 0).toFixed(2)} {payload.currency || "SAR"}
          </span>
        </div>

        <p className="yp-payment__hint">
          <bdi>
            {String(
              t("payThisAmount", "Pay this amount securely with your card.")
            )}
          </bdi>
        </p>

        {isStripeCheckout && stripeClientSecret ? (
          <PartnerStripeCheckout
            clientSecret={stripeClientSecret}
            onSuccess={async (paymentIntentId) => {
              const token =
                searchParams.get("token") || getPartnerTokenFromStorage();
              await BookingService.confirmPartnerStripePayment(
                payload.trip_id,
                paymentIntentId,
                token
              );
              navigate(
                withPartnerToken(
                  `/payment-form?type=success&message=Booking%20Added%20Successfully&bookingID=${payload.trip_id}`
                ),
                { replace: true }
              );
            }}
          />
        ) : isHyperPayCheckout ? (
          <div className="yp-payment__card">
            <PartnerHyperPayCheckout hyperpay={payload.hyperpay} />
          </div>
        ) : (
          <PartnerPayfortCheckout payForm={payfortForm} />
        )}
      </div>
    </div>
  );
}
